import { PayloadAction } from '@reduxjs/toolkit';
import { IRootState } from './types';
import { TPageInfo, TTransferQuantity, TVariant } from '../../types';
import { TProductTypeResponse } from '../../types/responses/inventory.type';

const reducers = {
  setVendors: (state: IRootState, action: PayloadAction<IRootState['vendors']>) => {
    const combinedVendors = [...state.vendors, ...action.payload];
    const uniqVendors = Array.from(new Set(combinedVendors));
    return {
      ...state, vendors: uniqVendors,
    };
  },
  setCategories: (state: IRootState, action: PayloadAction<IRootState['categories']>) => {
    const combinedCategories = [...state.categories, ...action.payload];
    const uniqCategories = Array.from(new Set(combinedCategories));
    return {
      ...state, categories: uniqCategories,
    };
  },
  setProductTypes: (state: IRootState, action: PayloadAction<IRootState['productTypes']>) => {
    const combinedProductTypes = [...state.productTypes, ...action.payload];
    const uniqProductTypes = Array.from(new Set(combinedProductTypes));
    return {
      ...state, productTypes: uniqProductTypes,
    };
  },
  setTags: (state: IRootState, action: PayloadAction<IRootState['tags']>) => {
    const combinedTags = [...state.tags, ...action.payload];
    const uniqTags = Array.from(new Set(combinedTags));
    return {
      ...state, tags: uniqTags,
    };
  },
  setSelectedLocationId: (state: IRootState, action: PayloadAction<IRootState['selectedLocationId']>) => ({
    ...state,
    selectedLocationId: action.payload,
  }),
  addVariantsToInventory: (state: IRootState, action: PayloadAction<{ locationId:number, variants:Array<TVariant> }>) => {
    const { locationId, variants } = action.payload;
    const upldatedLoadedCounter = state.inventoryItems?.[locationId]?.variants?.length;
    state.inventoryItems[locationId] = {
      ...state.inventoryItems[locationId],
      loadedInventoryCount: upldatedLoadedCounter! + variants.length,
      variants: [...(state.inventoryItems[locationId]?.variants || []), ...variants],
    };
  },
  setWorker: (state: IRootState, action: PayloadAction<{ locationId:number, worker:Worker }>) => {
    const { locationId, worker } = action.payload;

    state.inventoryItems[locationId] = {
      ...state.inventoryItems[locationId],
      currentWorker: worker,
    };
  },
  setCurrentWorker: (state: IRootState, action: PayloadAction<IRootState['currentWorker']>) => ({
    ...state,
    currentWorker: action.payload,
  }),
  addProductsToLocation: (state: IRootState, action: PayloadAction<{ locationId: number, products: Array<TProductTypeResponse>, pageInfo: TPageInfo }>) => {
    const { locationId, products, pageInfo } = action.payload;

    if (!state.inventoryItems[locationId]) {
      state.inventoryItems[locationId] = {
        totalInventoryCount: 0,
        loadedInventoryCount: 0,
        isLoading: false,
        products: {},
        pageInfo: { hasNextPage: false, endCursor: null },
        currentWorker: null,
        variants: [],
      };
    }

    if (!state.inventoryItems[locationId].products) {
      state.inventoryItems[locationId].products = {};
    }

    const updatedProducts = { ...state.inventoryItems[locationId].products };

    products.forEach((product) => {
      if (product.node?.id) {
        updatedProducts[product.node.id] = product.node;
      } else {
        console.error('Missing product node ID:', product);
      }
    });

    state.inventoryItems[locationId] = {
      ...state.inventoryItems[locationId],
      products: updatedProducts,
      pageInfo: {
        ...pageInfo,
        endCursor: pageInfo.endCursor,
      },
    };
  },
  setProductsIsLoading: (state: IRootState, action: PayloadAction<{ locationId:number, isLoading:boolean }>) => {
    const { isLoading, locationId } = action.payload;

    state.inventoryItems[locationId] = {
      ...state.inventoryItems[locationId],
      isLoading,
    };
  },
  setInventoryLocation: (state: IRootState, action: PayloadAction<IRootState['selectedLocationId']>) => {
    const locationId = action.payload;
    if (!state.inventoryItems[locationId!]) {
      state.inventoryItems[locationId!] = {
        totalInventoryCount: 0,
        loadedInventoryCount: 0,
        isLoading: false,
        products: {
        },
        pageInfo: {
          hasNextPage: true,
          endCursor: null,
        },
        currentWorker: null,
        variants: [],
      };
    }
  },
  setSelectedVariant: (state: IRootState, action: PayloadAction<TTransferQuantity | null>) => ({
    ...state,
    selectedVariant: action.payload,
  }),
  setUpdatedVariant: (state: IRootState, action: PayloadAction<{ variantId: string, quantityTypeName: string, quantityNumber: number, locationId: number }>) => {
    const {
      variantId, quantityTypeName, quantityNumber, locationId,
    } = action.payload;

    const numericLocationId = Number(locationId.toString().match(/\/(\d+)$/)?.[1]);
    const inventoryItems = state.inventoryItems[numericLocationId]?.variants;
    if (!inventoryItems) {
      console.warn('Location or inventory items not found');
      return;
    }

    state.inventoryItems[numericLocationId!]!.variants = inventoryItems.map((variant) => {
      if (variant.node.id === variantId) {
        return {
          ...variant,
          node: {
            ...variant.node,
            inventoryItem: {
              ...variant.node.inventoryItem,
              inventoryLevel: {
                ...variant.node.inventoryItem.inventoryLevel,
                quantities: variant.node.inventoryItem.inventoryLevel?.quantities.map((quantity) => {
                  if (quantity.name === quantityTypeName) {
                    return {
                      ...quantity,
                      quantity: (quantity.quantity || 0) - Number(quantityNumber),
                    };
                  }
                  return quantity;
                }) || [],
                location: variant!.node.inventoryItem!.inventoryLevel!.location || null,
              },
            },
          },
        };
      }
      return variant;
    });
  },
  setIsTransferWindowOpen: (state: IRootState, action: PayloadAction<IRootState['isTransferWindowOpen']>) => ({
    ...state,
    isTransferWindowOpen: action.payload,
  }),
};

export {
  reducers,
};
